import PropTypes from 'prop-types';
import { Text, Link } from '@nerdwallet/currency';
import Image from '~/app/components/image/image';
import { useEffect, useRef } from 'react';
import UniversalContainer, {
  universalContainerDefaultProps,
  universalContainerPropTypes,
} from '../universal-container';
import {
  boolCheckboxSpec,
  repeatable,
  stringInputSpec,
} from '../../lib/editing-configs';
import UniversalText from '../universal-text/universal-text';
import styles from './awards-2024-buttons.module.scss';

function getLinkUrl(linkUrl, shouldTrack) {
  const isAnchorLink = linkUrl.charAt(0) === '#';
  if (isAnchorLink || !shouldTrack) {
    return linkUrl;
  }

  return linkUrl;
}

const setStickyHeaderBehavior = (flexCardElement, stickyHeaderElement) => {
  if (!flexCardElement || !stickyHeaderElement) return;
  const stickyHeaderComponent = stickyHeaderElement;
  const elementHeight = 120;

  if (flexCardElement.getBoundingClientRect().top < -elementHeight) {
    stickyHeaderComponent.style.position = 'fixed';
    stickyHeaderComponent.style.top = 0;
  } else {
    stickyHeaderComponent.style.top = '-50%';
  }
};

const Awards2024ButtonsBox = ({
  boxIconUrl,
  boxTitle,
  boxLinkUrl,
  shouldTrack,
}) => {
  const url = getLinkUrl(boxLinkUrl, shouldTrack, boxTitle);

  return (
    <Link href={url} className={styles.boxLink} data-testid="button-box-link">
      <div className={styles.boxItem}>
        {boxIconUrl && (
          <Image
            className={styles.boxIcon}
            src={boxIconUrl}
            alt="box-icon"
            backgroundColor="transparent"
            loading="lazy"
            fetchPriority="low"
          />
        )}
        <div className={styles.boxTitle}>
          <Text>{boxTitle}</Text>
        </div>
      </div>
    </Link>
  );
};

Awards2024ButtonsBox.propTypes = {
  boxIconUrl: PropTypes.string,
  boxTitle: PropTypes.string,
  boxLinkUrl: PropTypes.string,
  shouldTrack: PropTypes.bool,
};

Awards2024ButtonsBox.defaultProps = {
  boxIconUrl: '',
  boxTitle: '',
  boxLinkUrl: '',
  shouldTrack: false,
};

const Awards2024Buttons = ({
  container,
  title,
  buttons,
  shouldTrack,
  disableStickyHeaderIntegration,
}) => {
  const awardsNavigationRef = useRef();

  useEffect(() => {
    const stickyHeader = document.getElementById('sticky-header-v2');
    if (stickyHeader) {
      stickyHeader.style.position = 'fixed';
      stickyHeader.style.display = 'flex';
      stickyHeader.style.top = '-50%';
    }
    const handleScroll = () => {
      setStickyHeaderBehavior(awardsNavigationRef.current, stickyHeader);
    };

    if (!disableStickyHeaderIntegration && stickyHeader) {
      window.addEventListener('scroll', handleScroll);
    } else if (stickyHeader) {
      stickyHeader.style.position = 'sticky';
      stickyHeader.style.top = '0';
    }
    return () => window.removeEventListener('scroll', handleScroll);
  }, [disableStickyHeaderIntegration]);

  return (
    <UniversalContainer
      {...container}
      data-testid="awards2024-buttons-container"
    >
      <UniversalText {...title} />
      <div className={styles.boxItemsContainer} ref={awardsNavigationRef}>
        {buttons?.map((box) => (
          <Awards2024ButtonsBox
            key={box.$unique_key}
            boxIconUrl={box.boxIconUrl}
            boxLinkUrl={box.boxLinkUrl}
            boxTitle={box.boxTitle}
            shouldTrack={shouldTrack}
          />
        ))}
      </div>
    </UniversalContainer>
  );
};

Awards2024Buttons.displayName = 'Awards2024Buttons';

Awards2024Buttons.propTypes = {
  children: PropTypes.node,
  container: universalContainerPropTypes,
  title: UniversalText.propTypes,
  buttons: PropTypes.array,
  shouldTrack: PropTypes.bool,
  disableStickyHeaderIntegration: PropTypes.bool,
};

Awards2024Buttons.defaultProps = {
  children: null,
  container: universalContainerDefaultProps,
  title: 'Awards2024Buttons',
  buttons: [],
  shouldTrack: false,
  disableStickyHeaderIntegration: false,
};

Awards2024Buttons.getEditingConfig = (componentDocument) => {
  return [
    ...UniversalContainer.getEditingConfig(
      componentDocument,
      'Container',
      ['padding', 'margin', 'backgroundColor', 'backgroundImage', 'jumpLinkId'],
      `container`
    ),
    ...UniversalText.getEditingConfig(
      componentDocument,
      'Title',
      [
        'fontWeight',
        'groupButton',
        'margin',
        'padding',
        'padding',
        'fontFamily',
        'textColor',
        'textSize',
        'textType',
      ],
      'title'
    ),
    boolCheckboxSpec(
      'shouldTrack',
      'Add BOA Awards Analytics Instrumentation?'
    ),
    boolCheckboxSpec(
      'disableStickyHeaderIntegration',
      'Disable StickyHeader Integration when Scrolling?'
    ),
    repeatable('buttons', 'Buttons', 'Button', () => [
      stringInputSpec('boxTitle', 'Button Title'),
      stringInputSpec('boxIconUrl', 'Button Icon URL'),
      stringInputSpec('boxLinkUrl', 'Button Link URL'),
    ]),
  ];
};
export default Awards2024Buttons;
